import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Star, Plus, Minus, ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";
import { useStore } from "@/context/StoreContext";
import { inr, sortedTiers, tierLabel, salePrice, tierDiscount } from "@/lib/api";
import { TiltCard } from "@/components/ChocolateFX";

export const ProductCard = ({ product, index = 0, preferTier }) => {
  const { inWishlist, toggleWishlist, addToCart } = useStore();
  const liked = inWishlist(product.product_id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      data-testid={`product-card-${product.slug}`}
    >
      <TiltCard className="lux-card hover-lift overflow-hidden group" max={7}>
        <div className="relative overflow-hidden aspect-[4/3]">
          <Link to={`/product/${product.slug}`}>
            <img src={product.image || product.images?.[0]} alt={product.name}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
          </Link>
          <div className="absolute inset-0 bg-gradient-to-t from-[#120A07] via-transparent to-transparent opacity-60 pointer-events-none" />
          {product.collection && (
            <span className="absolute top-3 left-3 text-[10px] tracking-widest uppercase bg-[#120A07]/80 text-[#E6C594] px-2.5 py-1 rounded-full border border-[#D4AF37]/30">{product.collection}</span>
          )}
          <button
            data-testid={`wishlist-btn-${product.slug}`}
            onClick={() => toggleWishlist(product.product_id)}
            className={`absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center backdrop-blur-md transition ${liked ? "bg-[#D4AF37] text-[#1A0F0B]" : "bg-[#120A07]/70 text-[#FDFBF7] hover:text-[#D4AF37]"}`}
          >
            <Heart size={16} fill={liked ? "#1A0F0B" : "none"} />
          </button>
          {Object.keys(product.tier_discounts || {}).length > 0 && (
            <span data-testid={`offer-badge-${product.slug}`} className="absolute bottom-3 right-3 text-[10px] font-bold tracking-wider bg-[#D4AF37] text-[#1A0F0B] px-2.5 py-1 rounded-full">
              {Object.keys(product.tier_discounts).length === sortedTiers(product.prices).length ? `UP TO ${Math.max(...Object.values(product.tier_discounts))}% OFF` : "OFFER ON SELECT PACKS"}
            </span>
          )}
          {product.stock <= 0 && (
            <span className="absolute bottom-3 left-3 text-xs bg-[#DC2626] text-white px-2 py-1 rounded">Sold out</span>
          )}
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-widest text-[#8C7B6E]">{product.category}</span>
            {product.rating_count > 0 && (
              <span className="flex items-center gap-1 text-xs text-[#E6C594]"><Star size={12} fill="#D4AF37" stroke="none" /> {product.rating_avg}</span>
            )}
          </div>
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-display text-lg text-[#FDFBF7] leading-snug hover:text-[#D4AF37] transition line-clamp-2">{product.name}</h3>
          </Link>
          <p className="text-[#8C7B6E] text-sm mt-1 line-clamp-2">{product.description}</p>
          <CardBuyer product={product} preferTier={preferTier} />
        </div>
      </TiltCard>
    </motion.div>
  );
};

const CardBuyer = ({ product, preferTier }) => {
  const { addToCart } = useStore();
  const tiers = sortedTiers(product.prices);
  const [tier, setTier] = useState(tiers.includes(preferTier) ? preferTier : tiers[0]);
  const disc = tierDiscount(product, tier);
  const [qty, setQty] = useState(1);
  const sid = product.slug;
  return (
    <div className="mt-4" data-testid={`card-buyer-${sid}`}>
      {tiers.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {tiers.map((t) => (
            <button key={t} data-testid={`card-tier-${sid}-${t}`} onClick={() => setTier(t)}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${tier === t ? "bg-[#D4AF37] text-[#1A0F0B] border-[#D4AF37] font-semibold" : "border-[#3B251D] text-[#C8B8AB] hover:border-[#D4AF37]"}`}>
              {tierLabel(t)}{tierDiscount(product, t) > 0 && <span className={`ml-1 text-[9px] font-bold ${tier === t ? "" : "text-[#D4AF37]"}`}>-{tierDiscount(product, t)}%</span>}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[10px] text-[#8C7B6E] block truncate">{tierLabel(tier)}</span>
          <span data-testid={`card-price-${sid}`} className="font-display text-lg sm:text-xl text-[#D4AF37]">{inr(salePrice(product, tier) * qty)}</span>
          {disc > 0 && <span className="ml-1.5 text-[11px] text-[#8C7B6E] line-through" data-testid={`card-mrp-${sid}`}>{inr(product.prices[tier] * qty)}</span>}
        </div>
        <div className="flex items-center border border-[#3B251D] rounded-full shrink-0">
          <button data-testid={`card-qty-minus-${sid}`} onClick={() => setQty(Math.max(1, qty - 1))} className="h-8 w-7 flex items-center justify-center text-[#D4AF37]"><Minus size={13} /></button>
          <span data-testid={`card-qty-${sid}`} className="w-5 text-center text-sm">{qty}</span>
          <button data-testid={`card-qty-plus-${sid}`} onClick={() => setQty(qty + 1)} className="h-8 w-7 flex items-center justify-center text-[#D4AF37]"><Plus size={13} /></button>
        </div>
      </div>
      <button data-testid={`quick-add-${sid}`} disabled={product.stock <= 0}
        onClick={() => { addToCart(product, tier, qty); setQty(1); }}
        className="btn-gold mt-3 w-full h-10 rounded-full flex items-center justify-center gap-2 text-sm disabled:opacity-40">
        <ShoppingBag size={15} /> {product.stock <= 0 ? "Sold out" : "Add to Cart"}
      </button>
    </div>
  );
};
